import React, { useEffect, useState, useCallback } from 'react';
import axios from '../api/axios';
import { Search, Settings, Users, RefreshCw, ChevronLeft } from 'lucide-react';
import MapComponent from '../components/Realtime/MapComponent';
import DeviceCard from '../components/Realtime/DeviceCard';
import MonitoringPanel from '../components/Realtime/MonitoringPanel';
import { data } from 'autoprefixer';

// Định nghĩa các Tab lọc trạng thái
const TABS = [
  { key: 'all', label: 'Tất cả' },
  { key: 'stopped', label: 'Dừng' },
  { key: 'running', label: 'Chạy' },
  { key: 'no_gps', label: 'Dừng GPS' },
  { key: 'lost', label: 'Mất kết nối' },
];

const getDeviceStatus = (device) => {
  if (!device) return 'lost';
  const now = Math.floor(Date.now() / 1000);
  const diff = now - (device.gpsdate_unix || 0);
  if (diff > 300) return 'lost';
  if (device.acc_status === 'On' && device.speed_kmh > 0) return 'running';
  if (device.acc_status === 'On') return 'stopped';
  return 'no_gps';
};

const RealTime = () => {
  const [devices, setDevices] = useState([]);           // Toàn bộ danh sách thiết bị
  const [loading, setLoading] = useState(true);          // Trạng thái đang tải dữ liệu
  const [selectedDevice, setSelectedDevice] = useState(null); // Thiết bị đang được chọn để xem chi tiết
  const [activeTab, setActiveTab] = useState('all');     // Tab trạng thái đang chọn (Tất cả, Chạy, Dừng...)
  const [search, setSearch] = useState('');              // Nội dung tìm kiếm IMEI
  const [sidebarOpen, setSidebarOpen] = useState(true);  // Trạng thái đóng/mở sidebar (dự phòng)

  const fetchRealTimeData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/logs/real-time');
      console.log(response?.data);

      let data = response?.data || [];

      setDevices(data);

      // Nếu chưa chọn thiết bị nào thì mặc định chọn thiết bị đầu tiên của danh sách mới
      if (data.length > 0 && !selectedDevice) {
        setSelectedDevice(data[0]);
      }
    } catch (error) {
      console.error('Lỗi lấy dữ liệu real-time:', error);
      // Fallback khi lỗi API
      setDevices(data);
      if (!selectedDevice) setSelectedDevice(data[0]);
    } finally {
      setLoading(false);
    }
  }, [selectedDevice]);


  /**
   * Hook xử lý việc gọi API lần đầu và định kỳ 30 giây một lần
   */
  useEffect(() => {
    fetchRealTimeData();
    const interval = setInterval(fetchRealTimeData, 30000);
    return () => clearInterval(interval);
  }, [fetchRealTimeData]);

  /**
   * Logic: Đếm số lượng thiết bị cho từng trạng thái
   */

  const counts = devices?.reduce((acc, d) => {
    const s = getDeviceStatus(d);
    acc[s] = (acc[s] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  /**
   * Logic: Lọc danh sách thiết bị dựa trên tab đang chọn và ô tìm kiếm
   */
  const filteredDevices = devices.filter((d) => {
    const matchTab = activeTab === 'all' || getDeviceStatus(d) === activeTab;
    const matchSearch = !search || d.imei.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  return (
    <div className="flex h-[calc(100vh-140px)] bg-gray-100 overflow-hidden">

      {/* ===== SIDEBAR TRÁI: DANH SÁCH THIẾT BỊ ===== */}
      {sidebarOpen && (
        <div className="w-[260px] flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full">

          {/* Header tài khoản */}
          <div className="px-3 py-2.5 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-[#7D7ACC] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-white">T</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-800 truncate">tomotek_demo</p>
                <p className="text-[10px] text-gray-500">{devices.length}/{devices.length}</p>
              </div>
              <button
                onClick={fetchRealTimeData}
                disabled={loading}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Làm mới"
              >
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Ô tìm kiếm */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 inset-y-0 my-auto flex text-gray-400 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Tất cả thiết bị..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-gray-100 rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 placeholder-gray-400"
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <Settings size={12} />
              </button>
            </div>
          </div>

          {/* Các tab đếm số lượng và lọc trạng thái */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="grid grid-cols-4 gap-1 text-center">
              {[
                { key: 'all', label: 'Tất cả', color: 'text-gray-700' },
                { key: 'stopped', label: 'Dừng', color: 'text-red-600' },
                { key: 'running', label: 'Chạy', color: 'text-green-600' },
                { key: 'lost', label: 'Mất kết nối', color: 'text-gray-500' },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => setActiveTab(item.key)}
                  className={`flex flex-col items-center py-1 px-0.5 rounded transition-colors hover:bg-gray-50 ${activeTab === item.key ? 'bg-[#F2F1FA]' : ''}`}
                >
                  <span className={`text-base font-black ${item.color}`}>{counts[item.key] || 0}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nút bộ lọc và Nhóm */}
          <div className="px-3 py-2 border-b border-gray-100 flex gap-2">
            <button className="flex items-center gap-1 bg-[#F2F1FA] text-[#7D7ACC] border border-[#7D7ACC] text-[11px] font-bold px-2 py-1 rounded flex-1 justify-center hover:bg-[#7D7ACC] hover:text-[#F2F1FA] transition-colors">
              ▼ Bộ lọc
            </button>
            <button className="flex items-center gap-1 bg-[#7D7ACC] text-white text-[11px] font-bold px-2 py-1 rounded flex-1 justify-center hover:bg-[#F2F1FA] hover:text-[#7D7ACC] transition-colors">
              <Users size={12} /> Nhóm
            </button>
          </div>

          {/* Nhãn nhóm và số lượng lọc được */}
          <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <input type="checkbox" defaultChecked className="w-3 h-3 accent-[#7D7ACC]" />
              <span className="text-[11px] font-bold text-gray-700">Mặc định ({filteredDevices.length})</span>
            </div>
            <button className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={14} />
            </button>
          </div>

          {/* Danh sách thiết bị (Scrollable) */}
          <div className="flex-1 overflow-y-auto">
            {loading && devices.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw size={20} className="animate-spin text-[#7D7ACC]" />
              </div>
            ) : filteredDevices.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-xs">Không có thiết bị</div>
            ) : (
              filteredDevices.map((device) => (
                <DeviceCard
                  key={device.imei}
                  device={device}
                  isSelected={selectedDevice?.imei === device.imei}
                  onClick={() => setSelectedDevice(device)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* ===== PHẦN CHÍNH: BẢN ĐỒ ===== */}
      <div className="flex-1 flex flex-col min-w-0">
        <MapComponent
          devices={filteredDevices}
          selectedDevice={selectedDevice}
          onSelectDevice={setSelectedDevice}
        />
      </div>

      {/* ===== SIDEBAR PHẢI: CHI TIẾT GIÁM SÁT ===== */}
      <MonitoringPanel device={selectedDevice} />
    </div>
  );
};

export default RealTime;